import type { ProjectListItem } from '@dispositio/shared';
import { LINK_STEPS } from './linkGuide';
import { SCHACH_STEPS } from './schachGuide';
import { TOUR_STEPS } from './tourGuide';
import { guideStepOf } from './tutorialGuide';

/**
 * Tutorials der Übersicht (Gruppe „Tutorials“, PLAN.md A102). Jedes hat ein Projekt mit eigener Vorlage (`projects.template`), das beim
 * ersten Öffnen entsteht; danach geht es dort weiter, „Neu beginnen“ ersetzt es durch ein frisches. Alle drei spielen am Demo-Roman
 * „Schachnovelle“ (PF 47, apps/server/src/services/exampleSchach.ts) und bauen in dieser Reihenfolge aufeinander auf, jedes mit einem
 * eigenen Thema: der „Rundgang“ (vollständig und ohne Fehler) durch die drei Modi, ihre Bedienelemente und die eigene erste Szene
 * (tourGuide.ts); „Verknüpfungen und Beziehungen“ (ohne Fehler, mit Lücken) Text, Zeit und Figuren verknüpfen (linkGuide.ts); die
 * „Fehlersuche“ (mit eingebauten Fehlern) prüfen und überblicken (schachGuide.ts).
 */
export interface TutorialInfo {
  key: string;
  title: string;
  /** Vorlage seines Projekts */
  template: string;
  /** Schritte der Führung (0: ohne Führung) */
  steps: number;
  /** Server-Aufruf, der ein frisches Projekt anlegt (ein früheres wird ersetzt) */
  path: string;
  /** auf der Karte, solange es noch nicht begonnen ist */
  intro: string;
}

export const TUTORIALS: TutorialInfo[] = [
  {
    key: 'rundgang',
    title: 'Rundgang',
    template: 'rundgang',
    steps: TOUR_STEPS.length,
    path: '/projects/tutorials/rundgang',
    intro: 'Der Einstieg: Bibliothek, Handlung und Manuskript – was die Bereiche und Knöpfe tun, an der „Schachnovelle“. Mit deiner ersten eigenen Szene.',
  },
  {
    key: 'verknuepfungen',
    title: 'Verknüpfungen und Beziehungen',
    template: 'verknuepfung',
    steps: LINK_STEPS.length,
    path: '/projects/tutorials/verknuepfungen',
    intro: 'Danach: wie du Text, Zeit und Figuren verknüpfst – bis zu Beziehungen und dem Tod einer Figur.',
  },
  {
    key: 'fehlersuche',
    title: 'Fehlersuche',
    template: 'schach',
    steps: SCHACH_STEPS.length,
    path: '/projects/tutorials/schach',
    intro: 'Zum Schluss: wie Dispositio Logikfehler findet und was die Übersichten über die ganze Geschichte zeigen.',
  },
];

/** Das Projekt eines Tutorials, falls es schon angelegt ist (das jüngste, falls es mehrere gäbe). */
export const tutorialProject = (t: TutorialInfo, projects: ProjectListItem[]): ProjectListItem | null =>
  projects.filter((p) => p.template === t.template).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))[0] ?? null;

/** Stand des Tutorials in diesem Browser: noch nicht begonnen, bei Schritt n (1 = erster), oder begonnen ohne laufende Führung. */
export function tutorialState(project: ProjectListItem | null, steps: number): { kind: 'new' } | { kind: 'step'; step: number } | { kind: 'open' } {
  if (!project) return { kind: 'new' };
  const step = guideStepOf(project.id, steps);
  return step === null ? { kind: 'open' } : { kind: 'step', step: step + 1 };
}
